import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Clock, Pencil, Save, X, Truck, Package, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { setDispatchEta } from "@/hooks/useUpdateDispatchStatus";

interface Props {
  dispatchId: string;
  loadId: string;
  pickupEtaAt: string | null;
  pickupEtaUpdatedAt: string | null;
  deliveryEtaAt: string | null;
  deliveryEtaUpdatedAt: string | null;
  editable: boolean;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId: string | null;
  onChanged?: () => void;
  /** Optional visual tone classes (fill/border/shadow) applied to each ETA card. Defaults to unchanged styling. */
  cardClassName?: string;
  /** When true the two cards are emitted bare, so the parent can place them in
   *  its own card grid. Default keeps the legacy 2-column wrapper. */
  inline?: boolean;
}

function relativeAgo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Split an ISO into a Date (date part) + "HH:mm" string for the two inputs. */
function splitIso(iso: string | null): { date: Date | undefined; time: string } {
  if (!iso) return { date: undefined, time: "" };
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return { date: d, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

/** Combine a Date (any time) + "HH:mm" back into an ISO string. */
function combineToIso(date: Date, time: string): string {
  const [h, m] = (time || "00:00").split(":").map((v) => parseInt(v, 10) || 0);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function EtaCard({
  icon: Icon, title, etaAt, updatedAt, editable, onSave, cardClassName,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  etaAt: string | null;
  updatedAt: string | null;
  editable: boolean;
  onSave: (iso: string | null) => Promise<void>;
  cardClassName?: string;
}) {
  const [editing, setEditing] = useState(false);
  const initial = splitIso(etaAt);
  const [datePart, setDatePart] = useState<Date | undefined>(initial.date);
  const [timePart, setTimePart] = useState<string>(initial.time || "09:00");
  const [calOpen, setCalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const startEditing = () => {
    const s = splitIso(etaAt);
    setDatePart(s.date);
    setTimePart(s.time || "09:00");
    setEditing(true);
  };

  const handleSave = async () => {
    if (!datePart) {
      toast.error("Pick a date first");
      return;
    }
    setBusy(true);
    try {
      await onSave(combineToIso(datePart, timePart));
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.message || "Could not update ETA");
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    setBusy(true);
    try {
      await onSave(null);
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.message || "Could not clear ETA");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className={`p-3 ${cardClassName ?? ""}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-brand-teal shrink-0" />
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground truncate">{title}</div>
        </div>
        {editable && !editing && (
          <Button size="sm" variant="ghost" className="h-6 gap-1 text-[11px] px-2" onClick={startEditing}>
            <Pencil className="h-3 w-3" /> {etaAt ? "Edit" : "Set"}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <div className="flex gap-1.5">
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-11 sm:h-8 flex-1 justify-start gap-1.5 text-xs font-normal",
                    !datePart && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {datePart ? format(datePart, "EEE, MMM d, yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={datePart}
                  onSelect={(d) => { setDatePart(d ?? undefined); setCalOpen(false); }}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <Input
              type="time"
              value={timePart}
              onChange={(e) => setTimePart(e.target.value)}
              className="h-11 w-[124px] text-xs tabular-nums sm:h-8 sm:w-[100px]"
              step={300}
              aria-label="Time (24-hour)"
            />
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" className="h-11 sm:h-7 text-xs flex-1 gap-1 bg-brand-teal hover:bg-brand-teal/90 text-white" onClick={handleSave} disabled={busy || !datePart}>
              <Save className="h-3 w-3" /> Save
            </Button>
            {etaAt && (
              <Button size="sm" variant="outline" className="h-11 sm:h-7 text-xs gap-1" onClick={handleClear} disabled={busy}>
                Clear
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-11 sm:h-7 text-xs gap-1" onClick={() => setEditing(false)} disabled={busy}>
              <X className="h-3 w-3" /> Cancel
            </Button>
          </div>
        </div>
      ) : etaAt ? (
        <div>
          <div className="text-base font-semibold text-foreground tabular-nums leading-tight">
            {format(new Date(etaAt), "EEE MMM d")}
          </div>
          <div className="text-sm text-foreground/80 tabular-nums">
            {format(new Date(etaAt), "h:mm a")}
          </div>
          {updatedAt && (
            <Badge variant="outline" className="mt-1.5 text-[10px] font-normal gap-1 border-border/60">
              <Clock className="h-2.5 w-2.5" /> updated {relativeAgo(updatedAt)}
            </Badge>
          )}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          {editable ? "No ETA set — click Set to add one" : "Awaiting broker ETA"}
        </div>
      )}
    </Card>
  );
}

export function DispatchETACards(props: Props) {
  const {
    dispatchId, loadId, pickupEtaAt, pickupEtaUpdatedAt, deliveryEtaAt, deliveryEtaUpdatedAt,
    editable, actorId, counterpartyUserId, shipperUserId, onChanged, cardClassName, inline,
  } = props;

  const save = async (kind: "pickup" | "delivery", iso: string | null) => {
    await setDispatchEta({
      dispatchId, loadId, kind, newAtIso: iso, actorId, counterpartyUserId, shipperUserId,
    });
    onChanged?.();
  };

  const cards = (
    <>
      <EtaCard
        icon={Truck}
        title="Truck ETA to Pickup"
        etaAt={pickupEtaAt}
        updatedAt={pickupEtaUpdatedAt}
        editable={editable}
        onSave={(iso) => save("pickup", iso)}
        cardClassName={cardClassName}
      />
      <EtaCard
        icon={Package}
        title="Estimated Delivery"
        etaAt={deliveryEtaAt}
        updatedAt={deliveryEtaUpdatedAt}
        editable={editable}
        onSave={(iso) => save("delivery", iso)}
        cardClassName={cardClassName}
      />
    </>
  );

  if (inline) return cards;
  return <div className="grid gap-3 sm:grid-cols-2 mb-4">{cards}</div>;
}
